import { useState, useEffect, useMemo, useRef } from 'react';
import { Button, IconButton, MixerLoader } from '../../components/Common';
import { useTranslation } from '../../hooks/useTranslation';
import { useHttpGetProjectsByRole, useHttpGetMembersOMX, useOrganizationId, useHttpUpdateProjectStatus } from '../../hooks';
import { ProjectDtoRelV2 } from '../../commonTypes/project.types';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../store/slices/auth.slice';
import { RoleTypeEnum } from '../../commonTypes/user.types';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import { selectOrganizationMembers } from '../../store/slices/add-edit-policy.slice';
import { ExpandableSearch } from '../../components/Common/ExpandableSearch';
import { Formik } from 'formik';
import { ProjectsTable } from './TableView';
import { ProjectsGridView } from './GridView';
import { ViewToggle } from '../../components/Projects';
import { ProjectStatus } from '@cyberlabrs/helpers';

interface IMenuItem {
    label: string;
    value: string;
}

const NoProjects = () => {
    const { t } = useTranslation('projects');
    const navigate = useNavigate();

    const authUser = useSelector(selectAuth);
    const isAuth =
        authUser.role.name !== RoleTypeEnum.ORDINARY && authUser.role.name !== RoleTypeEnum.BASIC && authUser.role.name !== RoleTypeEnum.TEAM_LEAD;

    return (
        <main className="no-project">
            <div>
                <img src="/svg/no-projects-2.svg" alt="No projects" className="no-projects__image" />

                <div className="no-project__content">
                    {isAuth ? (
                        <>
                            <h3>No Project Yet</h3>
                            <p>You don’t have any projects created. Start by adding your first project to get things going.</p>

                            <Button text={t('addNewProject')} onClick={() => navigate('/create-project')} />
                        </>
                    ) : (
                        <>
                            <h3>No Project Assigned</h3>
                            <p>You are currently not assigned to any project.</p>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
};

interface FormValues {
    search: string;
}

interface ProjectsProps {
    projects: ProjectDtoRelV2[];
    setProjects?: React.Dispatch<React.SetStateAction<ProjectDtoRelV2[]>>;
}

const Projects = ({ projects, setProjects }: ProjectsProps) => {
    const { isXSmall, isSmall } = useResizeListener();
    const isSmallScreen = isSmall || isXSmall;
    const { t } = useTranslation('projects');
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [tempFilters, setTempFilters] = useState(selectedFilters);
    const [areFiltersExpanded, setAreFiltersExpanded] = useState(false);
    const [view, setView] = useState<'list' | 'module'>('list');

    const filtersRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLImageElement>(null);

    const authUser = useSelector(selectAuth);
    const isAuth = ![RoleTypeEnum.ORDINARY, RoleTypeEnum.BASIC, RoleTypeEnum.TEAM_LEAD].includes(authUser.role.name);

    const { exec: updateProjectStatus } = useHttpUpdateProjectStatus();

    const menuItems: IMenuItem[] = [
        { label: 'In Progress', value: 'InProgress' },
        { label: 'Completed', value: 'Completed' },
        { label: 'On Hold', value: 'OnHold' },
        { label: 'Cancelled', value: 'Cancelled' },
        { label: 'Kick Off', value: 'KickOff' },
    ];

    const handleViewChange = (newView: 'list' | 'module') => {
        setView(newView);
    };

    const handleCheckboxChange = (value: string) => {
        setTempFilters(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
    };

    const handleApplyFilters = () => {
        setSelectedFilters(tempFilters);
        setAreFiltersExpanded(false);
    };

    const handleResetFilters = () => {
        setTempFilters([]);
        setSelectedFilters([]);
        setAreFiltersExpanded(false);
    };

    const handleUpdateProjectStatus = (projectId: string, newStatus: string) => {
        updateProjectStatus(projectId, newStatus as ProjectStatus, () => {
            if (setProjects) {
                setProjects(prevProjects =>
                    prevProjects.map(project => (project.id === projectId ? { ...project, projectStatus: newStatus as ProjectStatus } : project))
                );
            }
        });
    };

    return (
        <Formik<FormValues> initialValues={{ search: '' }} onSubmit={() => {}}>
            {formik => (
                <main className="entity-layout">
                    <div>
                        {isAuth && (
                            <div className="projects__header">
                                <ExpandableSearch
                                    id="search"
                                    placeholder="Search..."
                                    context={formik}
                                    className="invoices__search"
                                    setSearchTerm={setSearchTerm}
                                    isCloseButtonVisible={formik.values.search !== ''}
                                    closeButtonOnClick={() => {
                                        formik.setFieldValue('search', '');
                                        setSearchTerm('');
                                    }}
                                />

                                <div className="projects__header-container">
                                    <img
                                        ref={toggleRef}
                                        src={areFiltersExpanded ? '/svg/common/filters-blue.svg' : '/svg/common/filters.svg'}
                                        alt="Filter"
                                        onClick={() => setAreFiltersExpanded(prev => !prev)}
                                    />
                                    <ViewToggle value={view} onChange={handleViewChange} />
                                    {areFiltersExpanded && (
                                        <div ref={filtersRef} className="invoices__actions__filters">
                                            <p className="invoices__actions__filters__title">{'Status'}</p>
                                            <div className="invoices__actions__filters__options">
                                                {menuItems.map(item => (
                                                    <label key={item.value} className="invoices__actions__filters__checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={tempFilters.includes(item.value as string)}
                                                            onChange={() => handleCheckboxChange(item.value as string)}
                                                        />
                                                        <span>{item.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <div className="invoices__actions__filters__options__buttons">
                                                <Button text={'Apply Filters'} onClick={handleApplyFilters} />
                                                <Button text={'Reset Filters'} variant="text" onClick={handleResetFilters} color="primary" />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {isSmallScreen ? (
                                    <IconButton onClick={() => navigate('/create-project')} shape="circle" iconUrl="/svg/plus.svg" iconAlt="create" />
                                ) : (
                                    <Button text={t('addNewProject')} onClick={() => navigate('/create-project')} />
                                )}
                            </div>
                        )}
                        {view === 'list' ? (
                            <ProjectsTable projects={projects} searchTerm={searchTerm} selectedFilters={selectedFilters} />
                        ) : (
                            <ProjectsGridView
                                projects={projects}
                                searchTerm={searchTerm}
                                selectedFilters={selectedFilters}
                                onUpdateProjectStatus={handleUpdateProjectStatus}
                            />
                        )}
                    </div>
                </main>
            )}
        </Formik>
    );
};

const ProjectsSafetyWrapper = () => {
    const { isReady, projects: fetchedProjects } = useHttpGetProjectsByRole();
    const [projects, setProjects] = useState<ProjectDtoRelV2[]>([]);

    const organizationId = useOrganizationId();
    const { exec: fetchMembers } = useHttpGetMembersOMX(organizationId);
    const orgMembers = useSelector(selectOrganizationMembers);

    useEffect(() => {
        fetchMembers();
    }, []);

    const memberImageMap = useMemo(() => {
        const map = new Map<string, string>();
        orgMembers.forEach(member => {
            if (member.imageUrl) {
                map.set(member.userSub, member.imageUrl);
            }
        });
        return map;
    }, [orgMembers]);

    useEffect(() => {
        if (isReady && fetchedProjects.length > 0) {
            const projectsWithImages = fetchedProjects.map(project => ({
                ...project,
                projectAssignees:
                    project.projectAssignees?.map(assignee => ({
                        ...assignee,
                        imageUrl: assignee.imageUrl || memberImageMap.get(assignee.userSub) || undefined,
                    })) || [],
            }));
            setProjects(projectsWithImages);
        }
    }, [isReady, fetchedProjects, memberImageMap]);

    if (!isReady) {
        return <MixerLoader position="centerOnParent" />;
    }

    if (projects.length === 0) {
        return <NoProjects />;
    }

    return <Projects projects={projects} setProjects={setProjects} />;
};

export default ProjectsSafetyWrapper;
